import { KeyboardEventArgs } from '@syncfusion/ej2-base';
import { GridComponent } from '@syncfusion/ej2-react-grids';
import { RefObject } from 'react';
import { CurrentTOCItemState, ISelectedEntity } from '../contexts/formViewContext';
import { ColumnDto, ColumnGridDataType, ColumnType, DetailedWorkspaceDto, TaxReturnItemType, TaxReturnTOCItem } from '../model';
import { ROW_NO_COLUMN_NAME } from './DetailedWorkspace.constants';

export interface TableDataResult {
    columns: ColumnDto[];
    dataSource: Record<string, unknown>[];
}

export const DEFAULT_NUMBER_FORMAT = '#,###;(#,###);0';

export function transformColumnarReportToTableData(
    detailedWorkspace: DetailedWorkspaceDto | null | undefined,
    isColumnTable?: boolean
): TableDataResult {
    if (!detailedWorkspace?.columns || !detailedWorkspace?.rows) {
        return { columns: [], dataSource: [] };
    }
    const reportColumns: ColumnDto[] = detailedWorkspace.columns;
    const rows = detailedWorkspace.rows || [];

    const reportData = rows.map((row) => {
        const rowData: Record<string, unknown> = {};

        reportColumns.forEach((col) => {
            if (col.field) {
                const isRowNumberColumn = col.field === ROW_NO_COLUMN_NAME;
                const isFirstColumn = col.field === detailedWorkspace.firstColumn?.field;
                if (isColumnTable && !isFirstColumn && !isRowNumberColumn && !row[col.field]) {
                    const rowNo = row.RowNo as number;
                    const dataType = detailedWorkspace.rowSchema?.[rowNo]?.dataType;
                    rowData[col.field] = dataType === ColumnGridDataType.Numeric ? 0 : '';
                } else {
                    rowData[col.field] = row[col.field] ?? null;
                }
            }
        });
        return rowData;
    });

    return { columns: reportColumns, dataSource: reportData };
}

export function getGridColumnEditType(columnType?: ColumnType): string {
    if (columnType === ColumnType.Numeric) {
        return 'numeric';
    }

    return 'textbox';
}

export function getGridColumnType(columnType?: ColumnType): string {
    if (columnType === ColumnType.Numeric) {
        return 'number';
    }

    return 'string';
}

export interface IFormViewerMethods {
    getUpdatedDetailedWorkspace: () => DetailedWorkspaceDto;
    cleanUpState?: () => void;
    hasUnsavedEditChanges?: () => boolean;
    /** Restore focus to the primary editable element (e.g. textbox). Used when a dialog dismisses. */
    focus?: () => void;
}

export const parseNumericValue = (value: string = '') => {
    const numberString = value.replace(/\D/g, '');
    const finalNumber = Number(numberString);
    return finalNumber;
};

/** Display-only formatting for column-table cells where `rowSchema` marks the row as numeric. */
export function formatNumericCellDisplayForGrid(value: unknown): string {
    if (value === null || value === undefined || value === '') {
        return '';
    }
    let n: number;
    if (typeof value === 'number') {
        n = value;
    } else {
        const cleaned = String(value).replace(/,/g, '').trim();
        n = Number(cleaned);
    }
    if (Number.isNaN(n)) {
        return String(value);
    }
    const abs = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.abs(n));
    if (n < 0) {
        return `(${abs})`;
    }
    return abs;
}

export const buildSelectedStatement = (tocItem: TaxReturnTOCItem, entity?: ISelectedEntity): CurrentTOCItemState => {
    return {
        id: tocItem.id ?? '',
        dataName: tocItem.dataName,
        tocItemTitle: tocItem.title,
        taxReturnItemType: tocItem.taxReturnTOCKey?.taxReturnItemType,
        entity: entity,
        canSelectEntity: true,
        isConsolidated: tocItem.isConsolidated,
    };
};

export type ImperativeComponent<ITableFormViewerMethods, P extends object = object> = React.ForwardRefExoticComponent<
    React.PropsWithoutRef<P> & React.RefAttributes<ITableFormViewerMethods>
>;

export const isStatementEmpty = (
    detailedWorkspace: DetailedWorkspaceDto | null | undefined,
    taxReturnItemType: TaxReturnItemType | undefined
): boolean => {
    if (!detailedWorkspace) return true;

    switch (taxReturnItemType) {
        case TaxReturnItemType.CalculationDetailStatementColumn:
            return detailedWorkspace.columns?.length === 1; // Only has default column
        case TaxReturnItemType.CalculationDetailStatementRow:
            return !detailedWorkspace.rows?.length;
        case TaxReturnItemType.CalculationDetailStatementText:
            return !detailedWorkspace.text;
        default:
            return false;
    }
};

export const cancelEditModeOnShiftTab = (e: KeyboardEventArgs, innerGridRef: RefObject<GridComponent>) => {
    const closesttd = (e.target as HTMLElement)?.closest('td');
    if (closesttd?.classList?.contains('e-editedbatchcell') && e.key === 'Tab' && e.shiftKey) {
        innerGridRef.current?.editModule.saveCell();
    }
};
